'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn.js';
import {
  anchorScrollTarget,
  scrollElementBelowNav,
} from './anchor-scroll.js';
import {
  parseHomeAnchor,
  parseSamePageAnchor,
  shouldHandleAnchorClick,
} from './anchor-nav.js';
import { shouldRevealInViewport } from './reveal-visibility.js';

// ─────────────────────────────────────────────────────────────────────────────
// Revelação no scroll
// ─────────────────────────────────────────────────────────────────────────────

function revealElement(el: HTMLElement): void {
  el.dataset.revealed = 'true';
}

function elementShouldReveal(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return shouldRevealInViewport(rect.top, rect.bottom, rect.height, vh);
}

/** Revela todos os `.mc-reveal` visíveis (scroll, hash, arranque). */
export function revealVisibleElements(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('.mc-reveal:not([data-revealed="true"])').forEach(
    (el) => {
      if (elementShouldReveal(el)) revealElement(el);
    },
  );
}

const SECTION_ACTIVE_CLASS = 'mc-section-active';

/** Ao saltar para uma âncora, revela todo o conteúdo dessa secção de uma vez. */
export function revealSectionAnchors(hash: string): void {
  if (!hash || hash === '#') return;
  try {
    const id = hash.startsWith('#') ? hash.slice(1) : hash;
    const section =
      document.getElementById(id) ?? document.querySelector<HTMLElement>(hash);
    if (!section) return;

    for (const el of document.querySelectorAll<HTMLElement>(`.${SECTION_ACTIVE_CLASS}`)) {
      if (el !== section) el.classList.remove(SECTION_ACTIVE_CLASS);
    }
    section.classList.add(SECTION_ACTIVE_CLASS);
    section.querySelectorAll<HTMLElement>('.mc-reveal').forEach(revealElement);
  } catch {
    // Hash inválido — ignorar.
  }
}

/** Scroll + hash + revelação — funciona com `<a href="#…">` e com Next.js Link. */
export function navigateToAnchor(hash: string, scroll: ScrollBehavior = 'smooth'): void {
  if (!hash || hash === '#') return;
  const id = hash.startsWith('#') ? hash.slice(1) : hash;

  const apply = (): boolean => {
    const alvo = anchorScrollTarget(`#${id}`);
    if (!alvo) return false;
    // Revelar antes do scroll — evita secção “vazia” enquanto anima.
    revealSectionAnchors(`#${id}`);
    scrollElementBelowNav(alvo.scrollTarget, scroll);
    if (window.location.hash !== `#${id}`) {
      window.history.pushState(null, '', `#${id}`);
    }
    revealVisibleElements();
    return true;
  };

  if (!apply()) {
    for (const ms of [80, 250, 700]) {
      window.setTimeout(() => apply(), ms);
    }
  }

  for (const ms of [150, 500]) {
    window.setTimeout(() => {
      revealSectionAnchors(`#${id}`);
      revealVisibleElements();
    }, ms);
  }
}

/** Intercepta cliques em âncoras — captura antes do Next.js Link. */
export function installAnchorClickNavigation(): () => void {
  const onClick = (event: MouseEvent) => {
    if (!shouldHandleAnchorClick(event)) return;
    const anchor = (event.target as Element | null)?.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href) return;

    const loc = {
      pathname: window.location.pathname,
      origin: window.location.origin,
    };

    const samePage = parseSamePageAnchor(href, loc);
    if (samePage) {
      event.preventDefault();
      navigateToAnchor(samePage);
      return;
    }

    const homeHash = parseHomeAnchor(href, loc.origin);
    if (homeHash && loc.pathname === '/') {
      event.preventDefault();
      navigateToAnchor(homeHash);
      return;
    }

    if (homeHash && loc.pathname !== '/') {
      try {
        sessionStorage.setItem('mc-pending-hash', homeHash);
      } catch {
        /* modo privado */
      }
    }
  };

  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}

/**
 * Marca `data-mc-js="1"` no `<html>` para activar animações de revelação.
 * O conteúdo mantém-se legível (opacity: 1); sem JS, nada fica escondido.
 */
export function useRevealRoot(): void {
  useEffect(() => {
    document.documentElement.setAttribute('data-mc-js', '1');

    let scrollScheduled = false;
    const onScrollOrResize = () => {
      if (scrollScheduled) return;
      scrollScheduled = true;
      window.requestAnimationFrame(() => {
        scrollScheduled = false;
        revealVisibleElements();
      });
    };
    const onHash = () => {
      const hash = window.location.hash;
      if (hash) navigateToAnchor(hash, 'auto');
      else revealVisibleElements();
    };

    revealVisibleElements();
    if (window.location.hash) {
      navigateToAnchor(window.location.hash, 'auto');
    }

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    window.addEventListener('hashchange', onHash);
    const removeAnchorClick = installAnchorClickNavigation();

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('hashchange', onHash);
      removeAnchorClick();
    };
  }, []);
}

export type RevealTag = 'div' | 'li' | 'section' | 'article';

export interface RevealProps extends HTMLAttributes<HTMLElement> {
  /** Atraso em milissegundos, para escalonar itens de uma grelha. */
  delay?: number;
  as?: RevealTag;
}

export function Reveal({
  delay = 0,
  as = 'div',
  className,
  style,
  children,
  ...rest
}: RevealProps): ReactNode {
  const ref = useRef<HTMLElement>(null);
  // A tag é escolhida em runtime; o React não consegue estreitar a união de
  // tipos de props de cada elemento, por isso normalizamos aqui.
  const Tag = as as 'div';

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const tryReveal = (): boolean => {
      if (el.dataset.revealed === 'true') return true;
      if (elementShouldReveal(el)) {
        revealElement(el);
        return true;
      }
      return false;
    };

    // Sem IntersectionObserver, mostra já em vez de esconder para sempre.
    if (typeof IntersectionObserver === 'undefined') {
      revealElement(el);
      return;
    }

    if (tryReveal()) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealElement(entry.target as HTMLElement);
          io.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );

    io.observe(el);

    // Scroll/hash global: useRevealRoot() + revealVisibleElements().
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => tryReveal());
    });

    return () => {
      io.disconnect();
    };
  }, []);

  return (
    <Tag
      ref={ref as React.RefObject<HTMLDivElement>}
      className={cn('mc-reveal', className)}
      style={delay ? { ...style, transitionDelay: `${delay}ms` } : style}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Acordeão
// ─────────────────────────────────────────────────────────────────────────────

export interface AccordionItem {
  id: string;
  question: ReactNode;
  answer: ReactNode;
}

export interface AccordionProps {
  items: AccordionItem[];
  /** Permite ter vários painéis abertos ao mesmo tempo. */
  multiple?: boolean;
  defaultOpen?: string[];
  className?: string;
}

/**
 * `aria-expanded` não é opcional: é o que torna o componente acessível e é
 * também o ponto de ancoragem dos testes automatizados.
 */
export function Accordion({
  items,
  multiple = false,
  defaultOpen = [],
  className,
}: AccordionProps): ReactNode {
  const [open, setOpen] = useState<string[]>(defaultOpen);

  const toggle = useCallback(
    (id: string) => {
      setOpen((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id);
        return multiple ? [...prev, id] : [id];
      });
    },
    [multiple],
  );

  return (
    <div className={cn('divide-y divide-mc-line', className)} data-mc-accordion>
      {items.map((item) => (
        <AccordionRow
          key={item.id}
          item={item}
          open={open.includes(item.id)}
          onToggle={() => toggle(item.id)}
        />
      ))}
    </div>
  );
}

function AccordionRow({
  item,
  open,
  onToggle,
}: {
  item: AccordionItem;
  open: boolean;
  onToggle: () => void;
}): ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = `${item.id}-panel`;
  const buttonId = `${item.id}-button`;

  return (
    <div>
      <h3>
        <button
          type="button"
          id={buttonId}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          className={cn(
            'flex w-full items-center justify-between gap-4 py-5 text-left transition-colors',
            'min-h-[44px] hover:text-mc-accent',
            open ? 'text-mc-accent' : 'text-mc-ink',
          )}
        >
          <span className="text-mc-card-title font-medium">{item.question}</span>
          <span
            aria-hidden="true"
            className={cn(
              'grid size-7 shrink-0 place-items-center rounded-full border border-mc-line2 transition-transform duration-200',
              open && 'rotate-45 border-mc-accent',
            )}
          >
            <svg viewBox="0 0 12 12" className="size-3 fill-none stroke-current stroke-[1.5]">
              <path d="M6 1v10M1 6h10" strokeLinecap="round" />
            </svg>
          </span>
        </button>
      </h3>
      <div
        ref={panelRef}
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        className="overflow-hidden pb-6 text-mc-body text-mc-ink2"
      >
        {item.answer}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Abas
// ─────────────────────────────────────────────────────────────────────────────

export interface TabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
}

export function Tabs({
  items,
  className,
  panelClassName,
}: {
  items: TabItem[];
  className?: string;
  panelClassName?: string;
}): ReactNode {
  const [active, setActive] = useState(items[0]?.id ?? '');
  const groupId = useId();

  if (items.length === 0) return null;

  return (
    <div className={className}>
      <div
        role="tablist"
        className="flex flex-wrap gap-2 border-b border-mc-line pb-3"
      >
        {items.map((item) => {
          const selected = item.id === active;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`${groupId}-${item.id}-tab`}
              aria-selected={selected}
              aria-controls={`${groupId}-${item.id}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(item.id)}
              className={cn(
                'min-h-[40px] rounded-mc-pill px-5 text-sm font-medium transition-all duration-200',
                selected
                  ? 'bg-mc-accent/12 text-mc-accent shadow-mc-glow-sm ring-1 ring-mc-accent/50'
                  : 'text-mc-ink2 hover:bg-mc-surface-hover hover:text-mc-ink',
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          role="tabpanel"
          id={`${groupId}-${item.id}-panel`}
          aria-labelledby={`${groupId}-${item.id}-tab`}
          hidden={item.id !== active}
          className={cn('pt-6', panelClassName)}
        >
          {item.id === active ? item.content : null}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────────────────────────

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Cabeçalho mais compacto em telas baixas. */
  denseHeader?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  denseHeader = false,
}: ModalProps): ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Foca o primeiro campo editável — focar o painel impede digitar sem clicar antes.
    requestAnimationFrame(() => {
      const root = panelRef.current;
      if (!root) return;
      const alvo =
        root.querySelector<HTMLElement>(
          'textarea, input:not([type="hidden"]), select',
        ) ??
        root.querySelector<HTMLElement>('button:not([aria-label="Fechar"])');
      alvo?.focus();
    });

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const overlay = (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 px-4 pb-4 pt-[calc(var(--mc-nav-height,4rem)+var(--mc-modal-gap,0.75rem))] backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'flex w-full flex-col overflow-hidden rounded-mc-card-lg border border-mc-line2 bg-mc-bg2 shadow-mc-float outline-none',
          'max-h-[calc(100dvh-var(--mc-nav-height,4rem)-var(--mc-modal-gap,0.75rem)-1rem)]',
          size === 'sm' && 'max-w-md',
          size === 'md' && 'max-w-xl',
          size === 'lg' && 'max-w-3xl',
          size === 'xl' && 'max-w-5xl',
        )}
      >
        <header
          className={cn(
            'flex shrink-0 items-start justify-between gap-4 border-b border-mc-line',
            denseHeader ? 'p-4 sm:p-5' : 'p-4 sm:p-6',
          )}
        >
          <div className="min-w-0">
            <h2 id={titleId} className="text-mc-card-title font-semibold text-mc-ink">
              {title}
            </h2>
            {description ? (
              <p
                className={cn(
                  'text-mc-ink2',
                  denseHeader ? 'mt-0.5 text-xs sm:text-sm' : 'mt-1 text-sm',
                )}
              >
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="grid size-11 shrink-0 place-items-center rounded-full text-mc-ink3 transition-colors hover:bg-mc-surface-hover hover:text-mc-ink"
          >
            <svg viewBox="0 0 14 14" className="size-4 stroke-current stroke-[1.5]">
              <path d="M1 1l12 12M13 1L1 13" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 flex-wrap justify-end gap-3 border-t border-mc-line p-4 sm:p-6">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

// ─────────────────────────────────────────────────────────────────────────────
// Avisos
// ─────────────────────────────────────────────────────────────────────────────

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastContextValue {
  push: (tone: ToastTone, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, tone, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 6000);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (m) => push('success', m),
      error: (m) => push('error', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-6 right-6 z-[60] flex w-[min(24rem,calc(100vw-3rem))] flex-col gap-3"
        role="status"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto rounded-mc-card border px-4 py-3 text-sm shadow-mc-float backdrop-blur-md',
              t.tone === 'success' &&
                'border-mc-accent/50 bg-mc-bg2/95 text-mc-ink shadow-mc-glow-sm',
              t.tone === 'error' && 'border-mc-danger/50 bg-mc-bg2/95 text-mc-ink',
              t.tone === 'info' && 'border-mc-line2 bg-mc-bg2/95 text-mc-ink2',
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast precisa de estar dentro de <ToastProvider>.');
  }
  return ctx;
}
